import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { photoCredits, type PhotoCreditRow } from "../lib/api/lidoo.functions";

// Every freely licensed photo on the site, with its author, licence and
// source. CC BY and CC BY-SA require attribution wherever the picture is
// shown, and the club and region pages carry it; this page is the complete
// list, which is what a photographer looking for their own work will search
// for, and what "share-alike" and "attribution" are meant to make possible.
//
// The photos are of the coast NEAR a club, found on Wikimedia Commons by the
// club's coordinates (or on Openverse by town name), never presented as the
// club itself. The page says so, because that is the whole honesty of it.
//
// English only, like /how-we-verify: it is a record, not a landing page.
export const Route = createFileRoute("/photo-credits")({
  loader: async () => await photoCredits(),
  head: () => ({
    meta: [
      { title: "Photo credits | sunbedly" },
      {
        name: "description",
        content:
          "Authors, licences and sources of the coast photos on sunbedly: Pexels photographs on the homepage and region banners, Creative Commons photos (CC0, CC BY, CC BY-SA) near clubs.",
      },
    ],
    links: [{ rel: "canonical", href: "https://sunbedly.com/photo-credits" }],
  }),
  component: PhotoCreditsPage,
});

function CreditLine({ row }: { row: PhotoCreditRow }) {
  const link = "underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]";
  const name = row.credit_name ?? "Unknown author";
  return (
    <span className="text-[#5B6472]">
      {row.credit_url ? (
        <a
          href={row.credit_url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={link}
        >
          {name}
        </a>
      ) : (
        name
      )}
      {row.license_name ? (
        <>
          {" / "}
          {row.license_url ? (
            <a
              href={row.license_url}
              target="_blank"
              rel="license noopener noreferrer nofollow"
              className={link}
            >
              {row.license_name}
            </a>
          ) : (
            row.license_name
          )}
        </>
      ) : null}
    </span>
  );
}

function PhotoCreditsPage() {
  const { rows } = Route.useLoaderData();
  const regions = rows.filter((r) => r.kind === "region");
  const clubs = rows.filter((r) => r.kind === "club");

  // Grouped by coast, in the order the query returned them (region label,
  // then club name, then the photo's position).
  const byRegion = new Map<string, PhotoCreditRow[]>();
  for (const r of clubs) {
    const list = byRegion.get(r.region_label) ?? [];
    list.push(r);
    byRegion.set(r.region_label, list);
  }

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-12">
        <p className="li-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#2C4FD6]">
          Credits
        </p>
        <h1 className="li-serif mt-2 text-4xl font-normal leading-tight text-[#1C2430] md:text-5xl">
          Photo credits
        </h1>
        <p className="mt-3 max-w-[68ch] text-base leading-relaxed text-[#5B6472]">
          Some club and region pages show a photograph of the coast near the club rather than the
          club itself. Those pictures come from Wikimedia Commons and Openverse, were found by the
          club&apos;s coordinates or its town, and are used under a Creative Commons licence (CC0,
          CC BY or CC BY-SA). Each one is labelled &quot;Beach near ...&quot; on the page it appears
          on, with the author and the licence. The photograph on the homepage and the banners of
          the coasts are photographs from Pexels, chosen by hand for each coast and used under the{" "}
          <a
            href="https://www.pexels.com/license/"
            target="_blank"
            rel="license noopener noreferrer nofollow"
            className="underline decoration-[#2C4FD6] underline-offset-4"
          >
            Pexels License
          </a>
          , which asks for no credit; the photographer is named here anyway. Where no photo of
          the coast itself was good enough, the banner is a Mediterranean beach and its
          description says so. This is the full list. If one of these is yours and
          something is wrong with the credit, or you would rather it was not here, write to us and
          we take it down. Photos uploaded by clubs, and photos served from Google Places, are
          credited on their own pages and are not listed here. How the catalogue handles pictures in
          general is on{" "}
          <Link to="/how-we-verify" className="underline decoration-[#2C4FD6] underline-offset-4">
            /how-we-verify
          </Link>
          .
        </p>

        {rows.length === 0 ? (
          <p className="mt-10 text-base text-[#5B6472]">No licensed photos are in use yet.</p>
        ) : null}

        {regions.length > 0 ? (
          <section>
            <h2 className="li-serif mt-12 text-2xl font-normal text-[#1C2430] md:text-3xl">
              Coast banners
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              {regions.map((r) => (
                <li key={`region-${r.key}`} className="flex flex-wrap gap-x-2">
                  <Link
                    to="/region/$key"
                    params={{ key: r.key }}
                    className="text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]"
                  >
                    {r.name}
                  </Link>
                  <CreditLine row={r} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {[...byRegion.entries()].map(([label, list]) => (
          <section key={label}>
            <h2 className="li-serif mt-12 text-2xl font-normal text-[#1C2430] md:text-3xl">
              {label}
            </h2>
            <ul className="mt-4 space-y-2 text-sm">
              {list.map((r, i) => (
                <li key={`${r.key}-${i}`} className="flex flex-wrap gap-x-2">
                  <Link
                    to="/club/$slug"
                    params={{ slug: r.key }}
                    className="text-[#1C2430] underline decoration-[#2C4FD6] underline-offset-4 hover:text-[#2C4FD6]"
                  >
                    {r.name}
                  </Link>
                  <span className="text-[#5B6472]">(beach near {r.town})</span>
                  <CreditLine row={r} />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
      <Footer />
    </div>
  );
}
